'use client'

import { formatDistanceToNow } from 'date-fns'
import {
  BellIcon,
  CheckIcon,
  ChevronDownIcon,
  type LucideIcon,
  TriangleAlertIcon,
  XIcon,
} from 'lucide-react'
import { useLayoutEffect, useRef, useState } from 'react'

import { Button } from '@/components/ui/button'
import { ExchangeIcon } from '@/components/widget/exchange-icon'
import { cn } from '@/lib/utils'
import { formatSymbol } from '@/lib/utils/format-util'
import type { NotificationSeverity } from '@/service/ws/classify-notification'

interface NotificationSeverityStyle {
  icon: LucideIcon
  iconColor: string
  /** The card's left edge rule. */
  rule: string
}

/** Severity is carried by a bare glyph (no circles: square look, STYLE_GUIDE.md) and a thin
 * rule on the card's left edge; the card itself never changes background color. */
export const NOTIFICATION_SEVERITY_STYLE: Record<NotificationSeverity, NotificationSeverityStyle> =
  {
    success: { icon: CheckIcon, iconColor: 'text-chart-mint', rule: 'bg-chart-mint' },
    warning: { icon: TriangleAlertIcon, iconColor: 'text-ox-gold', rule: 'bg-ox-gold' },
    error: { icon: XIcon, iconColor: 'text-destructive', rule: 'bg-destructive' },
    info: { icon: BellIcon, iconColor: 'text-foreground/60', rule: 'bg-foreground/30' },
  }

export function formatRelativeTime(timestamp?: number): string {
  if (timestamp == null || Number.isNaN(timestamp)) return ''
  return formatDistanceToNow(timestamp, { addSuffix: true })
}

export interface NotificationToastCardProps {
  severity: NotificationSeverity
  title: string
  message?: string
  symbol?: string
  accountName?: string
  /** Exchange icon key (`tradeVenueToIconKey(account.tradeVenue)`) for the account badge
   * next to `accountName`. Without it the badge falls back to name-only text. */
  exchange?: string
  size: 'compact' | 'detailed'
  detail?: React.ReactNode
  timestamp?: number
  onClose?: () => void
  /** Optional call-to-action under the message, e.g. "Add account" on a missing-account error. */
  action?: { label: string; onClick: () => void }
}

/**
 * Shared visual shell for every toast in the app — WS-driven order/algo/security
 * notifications (notification-toast-helpers.tsx) and generic app toasts (lib/toast.tsx).
 * Solid bg-popover, not the translucent bg-card/backdrop-blur used by floating panels
 * (DraggablePopup) — toasts sit over live chart/price colors, where a
 * translucent card reads as low-contrast and hard to read.
 */
export function NotificationToastCard({
  severity,
  title,
  message,
  symbol,
  accountName,
  exchange,
  size,
  detail,
  timestamp,
  onClose,
  action,
}: NotificationToastCardProps) {
  const { icon: Icon, iconColor, rule } = NOTIFICATION_SEVERITY_STYLE[severity]

  // The message is clamped to two lines; a long error (e.g. an exchange rejection reason)
  // would be unreadable. Offer a toggle only when the text is actually cut off, so short
  // toasts stay exactly as they were.
  const messageRef = useRef<HTMLParagraphElement>(null)
  const [expanded, setExpanded] = useState(false)
  const [isTruncated, setIsTruncated] = useState(false)
  useLayoutEffect(() => {
    const el = messageRef.current
    // Once expanded nothing is clamped to measure; keep the last collapsed result so the
    // toggle stays available to collapse again.
    if (!el || expanded) return
    setIsTruncated(el.scrollHeight > el.clientHeight + 1)
  }, [message, expanded])

  return (
    <div
      className={cn(
        // pl-6 (instead of the old 8/10px) leaves a gutter for the absolutely-positioned
        // expand chevron, so every toast's icon/title starts at the same x and the
        // width/height of the card stay as before.
        'bg-popover border-foreground/10 relative border shadow-xl',
        // Same width for both sizes so stacked toasts line up; only the padding differs.
        'w-[min(22rem,calc(100vw-2rem))] pl-6',
        size === 'detailed' ? 'py-2.5 pr-2.5' : 'py-2 pr-2'
      )}
    >
      <span aria-hidden className={cn('absolute inset-y-0 left-0 w-0.5', rule)} />
      <div className="flex items-start gap-2">
        {/* Same affordance as the notification history rows: chevron at the far left of
            the header, pointing right when collapsed and down when expanded. Shown only
            when the message is actually cut off; absolutely positioned in the card's left
            gutter so it never takes layout space. */}
        {message && (isTruncated || expanded) && (
          <button
            type="button"
            onClick={() => setExpanded((prev) => !prev)}
            aria-expanded={expanded}
            aria-label={expanded ? 'Collapse message' : 'Show full message'}
            className={cn(
              'text-foreground/40 hover:bg-foreground/5 hover:text-foreground absolute left-1 flex size-5 cursor-pointer items-center justify-center',
              size === 'detailed' ? 'top-2.5' : 'top-2'
            )}
          >
            <ChevronDownIcon
              className={cn('size-3.5 transition-transform', !expanded && '-rotate-90')}
            />
          </button>
        )}
        {/* h-5 = the title's line box, so the glyph centers on the first line. */}
        <div className="flex h-5 w-3.5 shrink-0 items-center justify-center">
          <Icon className={cn('size-3.5', iconColor)} strokeWidth={2.5} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <span className="text-foreground min-w-0 text-sm leading-5 font-semibold tracking-tight break-words">
              {title}
            </span>
            {symbol && (
              <span
                className="text-foreground/40 shrink-0 truncate text-[10px] leading-5 font-medium tracking-tight uppercase"
                title={formatSymbol(symbol)}
              >
                {formatSymbol(symbol)}
              </span>
            )}
          </div>
          {message && (
            <p
              ref={messageRef}
              className={cn(
                'text-foreground/60 mt-0.5 text-xs break-words',
                expanded ? 'max-h-48 overflow-y-auto whitespace-pre-wrap' : 'line-clamp-2'
              )}
            >
              {message}
            </p>
          )}
          {detail}
          {action && (
            <Button variant="outline" size="xs" className="mt-1.5" onClick={action.onClick}>
              {action.label}
            </Button>
          )}
          {(timestamp !== undefined || accountName) && (
            <div className="mt-1 flex items-center justify-between gap-2">
              <span className="text-foreground/40 text-[10px]">
                {timestamp !== undefined && formatRelativeTime(timestamp)}
              </span>
              {accountName && (
                <span
                  className="text-foreground/40 flex min-w-0 shrink-0 items-center gap-1 text-[10px]"
                  title={accountName}
                >
                  {exchange && (
                    <ExchangeIcon exchange={exchange} className="size-3 shrink-0 rounded-full" />
                  )}
                  <span className="max-w-24 truncate">{accountName}</span>
                </span>
              )}
            </div>
          )}
        </div>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="text-foreground/40 hover:bg-foreground/5 hover:text-foreground flex size-5 shrink-0 cursor-pointer items-center justify-center transition-colors"
            aria-label="Dismiss"
          >
            <XIcon className="size-3.5" />
          </button>
        )}
      </div>
    </div>
  )
}
